import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Brain, Cpu } from 'lucide-react';
import InteractiveBrain from '../brain/InteractiveBrain';
import TypingEffect from '../ui/TypingEffect';

const Hero: React.FC = () => {
  const messages = [
    "Data Architecture for Companies That Scale",
    "Human Intelligence × Artificial Intelligence",
    "Transform Data into Strategic Advantage"
  ];

  return (
    <section id="home" className="section-padding relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-ariyva-indigo/5 via-transparent to-ariyva-cyan/5" />
      
      <div className="relative max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left column */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-ariyva-indigo/10 to-ariyva-cyan/10 border border-ariyva-cyan/20">
                <Sparkles className="w-4 h-4 text-ariyva-amber" />
                <span className="text-sm font-semibold text-ariyva-indigo">
                  Where Knowledge Meets Technology
                </span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="block">Build</span>
                <span className="gradient-text">Intelligent</span>
                <span className="block">Data Foundations</span>
              </h1>
              
              <div className="h-16">
                <TypingEffect messages={messages} speed={50} delay={2000} />
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg text-gray-600"
            >
              We design and implement modern data platforms that power growth, 
              efficiency, and intelligent decision-making. From startup to enterprise, 
              we build scalable foundations.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-4"
            >
              <button className="group px-8 py-4 bg-gradient-to-r from-ariyva-indigo to-ariyva-electric text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center hover:scale-[1.02]">
                Book Strategy Session
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              
              <button className="px-8 py-4 border-2 border-ariyva-cyan text-ariyva-indigo font-semibold rounded-xl hover:bg-ariyva-cyan/10 transition-all duration-300 hover:scale-[1.02]">
                View Our Solutions
              </button>
            </motion.div>
          </div>

          {/* Right column - Brain visualization */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <InteractiveBrain />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;